import React, { useEffect, useMemo, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { getInventoryFilters } from '../services/project/inventoryService';

const getLocalDateInputValue = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

const InventoryQueryFilters = ({
  initialValues,
  onApply,
  isLoading = false,
}) => {
  const { projectId } = useParams();
  const today = getLocalDateInputValue();
  const [values, setValues] = useState({
    search: '',
    region: '',
    area: '',
    startDate: today,
    endDate: today,
  });
  const [filterData, setFilterData] = useState([]);

  useEffect(() => {
    getInventoryFilters()
      .then((res) => {
        const data = res?.data || {};
        const regions = data[projectId] || [];
        setFilterData(regions);
      })
      .catch(() => setFilterData([]));
  }, [projectId]);

  useEffect(() => {
    setValues({
      search: initialValues?.search || '',
      region: initialValues?.region || '',
      area: initialValues?.area || '',
      startDate: initialValues?.startDate || today,
      endDate: initialValues?.endDate || today,
    });
  }, [initialValues, today]);

  const areaOptions = useMemo(() => {
    if (!values.region) {
      return filterData.flatMap((r) => (r.areas || []).map((a) => ({ id: a.id, name: a.name })));
    }
    const region = filterData.find((r) => String(r.id) === String(values.region));
    return (region?.areas || []).map((a) => ({ id: a.id, name: a.name }));
  }, [filterData, values.region]);

  const isStartFuture = !!values.startDate && values.startDate > today;
  const isEndFuture = !!values.endDate && values.endDate > today;
  const isRangeInvalid = !!values.startDate && !!values.endDate && values.startDate > values.endDate;
  const hasDateError = isStartFuture || isEndFuture || isRangeInvalid;

  const updateValue = (key, nextValue) => {
    setValues((prev) => ({ ...prev, [key]: nextValue }));
  };

  const handleRegionChange = (selected) => {
    setValues((prev) => ({ ...prev, region: selected ? String(selected.value) : '', area: '' }));
  };

  const handleApply = () => {
    if (hasDateError) return;
    onApply?.({ ...values });
  };

  return (
    <div className="d-flex flex-wrap gap-2 mb-3">
      <Form.Control
        placeholder="Search user..."
        value={values.search}
        onChange={e => updateValue('search', e.target.value)}
        style={{ maxWidth: 340 }}
      />
      <Select
        isClearable
        isSearchable
        options={filterData.map((r) => ({ value: r.id, label: r.name }))}
        value={filterData.filter((r) => String(r.id) === String(values.region)).map((r) => ({ value: r.id, label: r.name }))[0] || null}
        onChange={handleRegionChange}
        placeholder="All Regions"
        menuPortalTarget={document.body}
        styles={{ container: (b) => ({ ...b, minWidth: 180, maxWidth: 220 }) }}
      />
      <Select
        isClearable
        isSearchable
        options={areaOptions.map((a) => ({ value: a.id, label: a.name }))}
        value={areaOptions.filter((a) => String(a.id) === String(values.area)).map((a) => ({ value: a.id, label: a.name }))[0] || null}
        onChange={(selected) => updateValue('area', selected ? String(selected.value) : '')}
        placeholder="All Areas"
        menuPortalTarget={document.body}
        styles={{ container: (b) => ({ ...b, minWidth: 180, maxWidth: 220 }) }}
      />
      <Form.Control
        type="date"
        value={values.startDate}
        onChange={e => updateValue('startDate', e.target.value)}
        max={today}
        isInvalid={isStartFuture || isRangeInvalid}
        style={{ maxWidth: 170 }}
      />
      <Form.Control
        type="date"
        value={values.endDate}
        onChange={e => updateValue('endDate', e.target.value)}
        min={values.startDate || undefined}
        max={today}
        isInvalid={isEndFuture || isRangeInvalid}
        style={{ maxWidth: 170 }}
      />
      <Button onClick={handleApply} disabled={isLoading || hasDateError}>Filter</Button>
      {hasDateError && (
        <div className="text-danger small align-self-center">
          Invalid date range. Dates cannot be in the future, and From date cannot be greater than To date.
        </div>
      )}
    </div>
  );
};

export default InventoryQueryFilters;
